import React from 'react';
import { herbBatches } from '../data/herbData';

const Provenance = () => {
  // Using the first batch as sample data
  const batch = herbBatches['ASH001'];

  const MapPlaceholder = ({ gps, location }) => (
    <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-200 to-green-200"></div>
      <div className="relative z-10 text-center">
        <div className="text-4xl mb-2">📍</div>
        <p className="text-sm font-medium text-gray-700">{location}</p>
        <p className="text-xs text-gray-600">
          {gps.latitude.toFixed(4)}, {gps.longitude.toFixed(4)}
        </p>
        <p className="text-xs text-gray-500 mt-2">Interactive map placeholder</p>
      </div>
    </div>
  );

  const StatusBadge = ({ status, label }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {status ? '✓' : '✗'} {label}
    </span>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Herb Provenance</h1>
        <p className="mt-2 text-lg text-gray-600">
          Complete traceability information for batch {batch.id}
        </p>
      </div>

      {/* Herb Overview */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{batch.herbName}</h2>
            <p className="text-gray-600 italic">{batch.botanicalName}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Batch ID</p>
            <p className="text-lg font-mono font-semibold text-primary-600">{batch.id}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            batch.blockchain.verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {batch.blockchain.verified ? '✓ Blockchain Verified' : '✗ Not Verified'}
          </div>
          <p className="text-sm text-gray-500">
            Hash: <span className="font-mono">{batch.blockchain.hash}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Farmer Information */}
        <div className="card">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Farmer Information</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Farmer Name</p>
              <p className="text-lg font-medium text-gray-900">{batch.farmer.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="text-gray-900">{batch.farmer.location}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">GPS Coordinates</p>
              <p className="text-gray-900 font-mono">
                {batch.farmer.gps.latitude}, {batch.farmer.gps.longitude}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Harvest Date</p>
              <p className="text-gray-900">{new Date(batch.harvestDate).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="card">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Farm Location</h3>
          <MapPlaceholder gps={batch.farmer.gps} location={batch.farmer.location} />
        </div>
      </div>

      {/* Processing Steps */}
      <div className="card mt-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Processing Steps</h3>
        <div className="space-y-4">
          {batch.processing.map((step, index) => (
            <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-grow">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-900">{step.step}</h4>
                  <span className="text-sm text-gray-500">{new Date(step.date).toLocaleDateString()}</span>
                </div>
                <p className="text-gray-600 mt-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Lab Test Results */}
        <div className="card">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Lab Test Results</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600">Moisture Content</span>
              <span className="font-medium text-gray-900">{batch.labTests.moisture}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600">Pesticide Residue</span>
              <span className="font-medium text-green-600">{batch.labTests.pesticide}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600">Heavy Metals</span>
              <span className="font-medium text-green-600">{batch.labTests.heavyMetals}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600">Microbiology</span>
              <span className="font-medium text-green-600">{batch.labTests.microbiology}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600">Active Compounds</span>
              <span className="font-medium text-gray-900">{batch.labTests.withanolides}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Tested by: {batch.labTests.labName}
              </p>
              <p className="text-sm text-gray-500">
                Test Date: {new Date(batch.labTests.testDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Sustainability Compliance */}
        <div className="card">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Sustainability Compliance</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Organic Certified</span>
              <StatusBadge status={batch.sustainability.organic} label="Organic" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Fair Trade</span>
              <StatusBadge status={batch.sustainability.fairTrade} label="Fair Trade" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Carbon Neutral</span>
              <StatusBadge status={batch.sustainability.carbonNeutral} label="Carbon Neutral" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Water Conservation</span>
              <StatusBadge status={batch.sustainability.waterConservation} label="Water Conserved" />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-gray-200 mt-4">
              <span className="text-gray-600">Soil Health</span>
              <span className={`font-medium ${
                batch.sustainability.soilHealth === 'Excellent' ? 'text-green-600' :
                batch.sustainability.soilHealth === 'Good' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {batch.sustainability.soilHealth}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Blockchain Information */}
      <div className="card mt-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Blockchain Verification</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Transaction Hash</p>
              <p className="text-sm font-mono text-gray-900 break-all">{batch.blockchain.hash}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Timestamp</p>
              <p className="text-sm text-gray-900">{new Date(batch.blockchain.timestamp).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className={`text-sm font-medium ${
                batch.blockchain.verified ? 'text-green-600' : 'text-red-600'
              }`}>
                {batch.blockchain.verified ? 'Verified ✓' : 'Pending ⏳'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Provenance;
